"use client";

import { useState, useSyncExternalStore } from "react";

// =============================================================================
// Tiny store telling the page when it is on stage: once the intro preloader
// has finished, and while no page transition covers the screen. Entrance
// animations wait for it, so nothing plays under a curtain.
// =============================================================================

let introDone = false;
let covered = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function markIntroDone(): void {
  if (introDone) return;
  introDone = true;
  emit();
}

/** A page transition covers (true) or uncovers (false) the screen */
export function setStageCovered(value: boolean): void {
  if (covered === value) return;
  covered = value;
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A store value that stays true once it has been true for the component */
function useLatched(getSnapshot: () => boolean): boolean {
  const value = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const [wasTrue, setWasTrue] = useState(false);
  if (value && !wasTrue) setWasTrue(true);
  return value || wasTrue;
}

/**
 * True once the component has been on stage (preloader over, no curtain
 * down), and then for good: the page that a curtain is covering keeps its
 * revealed content, only the pages mounted under it wait.
 */
export function useIntroDone(): boolean {
  return useLatched(() => introDone && !covered);
}

/**
 * Same, without waiting for the preloader (shared entrance animations play
 * under it on a first visit, as the largest paint must not wait): only a page
 * mounted under a transition curtain waits for it to lift.
 */
export function useUncovered(): boolean {
  return useLatched(() => !covered);
}

// =============================================================================
